import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Notification } from '../types/notification';
import { notificationService } from '../services/notificationService';
import { messageService } from '../services/messageService';
import { io, Socket } from 'socket.io-client';
import {SOCKET_URL} from "../config/api.config";

interface NotificationContextProps {
    notifications: Notification[];
    unreadCount: number;
    unreadMessagesCount: number;
    isLoading: boolean;
    error: string | null;
    markAsRead: (notificationId: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteNotification: (notificationId: string) => Promise<void>;
    clearAllNotifications: () => Promise<void>;
    refreshNotifications: () => Promise<void>;
    // Nouvelles méthodes pour gérer la conversation active
    setActiveConversation: (conversationId: string | null) => void;
    getActiveConversation: () => string | null;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

interface NotificationProviderProps {
    children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [socket, setSocket] = useState<Socket | null>(null);
    // Nouvelle variable d'état pour la conversation active
    const [activeConversation, setActiveConversation] = useState<string | null>(null);

    // Initialisation du socket et chargement des notifications
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user || !user._id) return;

        // Initialiser la connexion WebSocket
        const socketInstance = io(SOCKET_URL, {
            withCredentials: true
        });

        setSocket(socketInstance);

        socketInstance.on('connect', () => {
            console.log('Socket connecté');

            // Enregistrer l'utilisateur avec son ID
            socketInstance.emit('register', { userId: user._id });
        });

        // Écouter les événements de notification
        socketInstance.on('notification', (newNotification: Notification) => {
            console.log('Nouvelle notification reçue:', newNotification);

            // Vérifier si la notification concerne la conversation active
            // Note: La structure exacte de newNotification.data dépend de votre implémentation
            const isForActiveConversation = activeConversation &&
                newNotification.type === 'NEW_MESSAGE' &&
                newNotification.data &&
                newNotification.data.conversationId === activeConversation;

            if (!isForActiveConversation) {
                // Ajouter la nouvelle notification au début de la liste seulement si elle ne concerne pas la conversation active
                setNotifications(prev => [newNotification, ...prev]);

                // Incrémenter le compteur si la notification n'est pas lue
                if (!newNotification.read) {
                    setUnreadCount(prev => prev + 1);
                }
            } else {
                console.log('Notification ignorée car conversation active:', activeConversation);
            }
        });

        socketInstance.on('chat-message', (message) => {
            console.log('Nouveau message reçu:', message);

            // Ne pas mettre à jour le compteur si le message concerne la conversation active
            const conversationId = message.conversationId;
            if (activeConversation !== conversationId) {
                // Rafraîchir le compteur de messages non lus
                loadUnreadMessagesCount();
            } else {
                console.log('Message ignoré pour le compteur car conversation active:', activeConversation);
            }
        });

        // Charger les notifications et les compteurs
        refreshNotifications();

        // Nettoyage à la déconnexion
        return () => {
            if (socketInstance) {
                socketInstance.disconnect();
            }
        };
    }, [activeConversation]); // Ajout de activeConversation comme dépendance

    // Getters et setters pour la conversation active
    const getActiveConversation = () => activeConversation;
    const handleSetActiveConversation = (conversationId: string | null) => {
        setActiveConversation(conversationId);
        console.log('Conversation active définie:', conversationId);

        // Si une conversation devient active, on peut immédiatement marquer les messages comme lus
        if (conversationId) {
            // Filtrer les notifications pour cette conversation et les marquer comme lues
            const notificationsToMarkAsRead = notifications.filter(notif =>
                notif.type === 'NEW_MESSAGE' &&
                notif.data &&
                notif.data.conversationId === conversationId &&
                !notif.read
            );

            // Marquer chaque notification comme lue
            notificationsToMarkAsRead.forEach(async notif => {
                if (notif._id) {
                    await markAsRead(notif._id);
                }
            });
        }
    };

    // Charger les notifications
    const loadNotifications = async () => {
        try {
            const data = await notificationService.getNotifications();
            setNotifications(data);
        } catch (err) {
            console.error('Erreur lors du chargement des notifications:', err);
        }
    };

    // Charger le nombre de notifications non lues
    const loadUnreadCount = async () => {
        try {
            const count = await notificationService.getUnreadCount();
            setUnreadCount(count);
        } catch (err) {
            console.error('Erreur lors du chargement du nombre de notifications non lues:', err);
        }
    };

    // Charger le nombre de messages non lus
    const loadUnreadMessagesCount = async () => {
        try {
            const count = await messageService.getUnreadMessagesCount();
            setUnreadMessagesCount(count);
        } catch (err) {
            console.error('Erreur lors du chargement du nombre de messages non lus:', err);
        }
    };

    // Rafraîchir toutes les données
    const refreshNotifications = async () => {
        setIsLoading(true);
        try {
            await Promise.all([
                loadNotifications(),
                loadUnreadCount(),
                loadUnreadMessagesCount()
            ]);
            setError(null);
        } catch (err) {
            setError('Erreur lors du rafraîchissement des données');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // Marquer une notification comme lue
    const markAsRead = async (notificationId: string) => {
        try {
            await notificationService.markAsRead(notificationId);

            // Mettre à jour l'état local
            setNotifications(prev =>
                prev.map(notif =>
                    notif._id === notificationId
                        ? { ...notif, read: true }
                        : notif
                )
            );

            // Diminuer le compteur de non lus
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Erreur lors du marquage de la notification comme lue:', err);
        }
    };

    // Marquer toutes les notifications comme lues
    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();

            // Mettre à jour l'état local
            setNotifications(prev =>
                prev.map(notif => ({ ...notif, read: true }))
            );

            // Réinitialiser le compteur
            setUnreadCount(0);
        } catch (err) {
            console.error('Erreur lors du marquage de toutes les notifications comme lues:', err);
        }
    };

    // Supprimer une notification
    const deleteNotification = async (notificationId: string) => {
        try {
            await notificationService.deleteNotification(notificationId);

            // Trouver la notification avant de la supprimer pour ajuster le compteur
            const notifToDelete = notifications.find(n => n._id === notificationId);

            // Mettre à jour l'état local
            setNotifications(prev =>
                prev.filter(notif => notif._id !== notificationId)
            );

            // Ajuster le compteur si la notification n'était pas lue
            if (notifToDelete && !notifToDelete.read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Erreur lors de la suppression de la notification:', err);
        }
    };

    // Supprimer toutes les notifications
    const clearAllNotifications = async () => {
        try {
            await notificationService.clearAllNotifications();

            // Mettre à jour l'état local
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            console.error('Erreur lors de la suppression de toutes les notifications:', err);
        }
    };

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                unreadMessagesCount,
                isLoading,
                error,
                markAsRead,
                markAllAsRead,
                deleteNotification,
                clearAllNotifications,
                refreshNotifications,
                // Exposer les nouvelles méthodes pour la conversation active
                setActiveConversation: handleSetActiveConversation,
                getActiveConversation
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = (): NotificationContextProps => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};